import { useEffect, useState, useSyncExternalStore } from 'react'
import type { PullRequestMetadata, WebTestRun } from '@blume/web-test-model'
import {
  Ban,
  Check,
  Eye,
  GitMerge,
  GitPullRequest,
  GitPullRequestClosed,
  GitPullRequestDraft,
  LoaderCircle,
  ScanSearch,
  TimerOff,
  UserRound,
  X,
  Zap
} from 'lucide-react'

import { runVerdict } from '@/design/status'
import { cn } from '@/lib/utils'

/** Broad deterministic identity pool for legacy runs that predate authored emoji. */
export const fallbackEmojis = [
  '🧪',
  '🧩',
  '🔎',
  '📋',
  '🛰️',
  '🧭',
  '🪴',
  '🧱',
  '🎛️',
  '🗂️',
  '🔐',
  '🧰',
  '⚙️',
  '🪄',
  '🧵',
  '🪟',
  '🗺️',
  '🔭',
  '💡',
  '🧠',
  '🦋',
  '🌻',
  '🍀',
  '🌈',
  '🐙',
  '🦊',
  '🐝',
  '🦄',
  '🐳',
  '🦜',
  '🍋',
  '🍓',
  '🥝',
  '🍄',
  '🌋',
  '🏔️',
  '🚲',
  '🚀',
  '🛶',
  '🎈',
  '🎨',
  '🎲',
  '🎯',
  '🎹',
  '📚',
  '✏️',
  '🪁',
  '🧸',
  '🪐',
  '🌙',
  '☀️',
  '⭐',
  '🔔',
  '🪙',
  '🧲',
  '🔬',
  '🧯',
  '🪜',
  '🪞',
  '🧷',
  '🗝️',
  '🪶',
  '💎',
  '🫧',
  '🌵',
  '🪼',
  '🐢',
  '🦀',
  '🦖',
  '🦕',
  '🦩',
  '🦦'
]

/** A progressively enhanced squircle with a robust rounded fallback. */
export const avatarSquircleClass = 'rounded-[32%] [corner-shape:squircle]'

export function hash(value: string): number {
  let hashValue = 2_166_136_261
  for (const character of value)
    hashValue = Math.imul(hashValue ^ character.charCodeAt(0), 16_777_619)
  return hashValue >>> 0
}

/** A stable visual identity when the persisted scenario definition is not loaded. */
export function scenarioEmoji(id: string): string {
  return fallbackEmojis[hash(id) % fallbackEmojis.length]!
}

/** Kept while dashboard callers migrate to the plainly named helper. */
export const legacyScenarioEmoji = scenarioEmoji

export type Rgb = readonly [number, number, number]
export type AvatarSize = 'small' | 'regular' | 'large'
export type RunAvatarVariant = 'centered' | 'corner'

const tileSizeClass: Record<AvatarSize, string> = {
  small: 'size-7 text-sm',
  regular: 'size-8 text-base',
  large: 'size-9 text-lg'
}
const suiteClusterPixels: Record<AvatarSize, number> = {
  small: 14,
  regular: 16,
  large: 18
}
const suiteEmojiPixels: Record<AvatarSize, number> = {
  small: 6,
  regular: 7,
  large: 8
}
const pullRequestSizeClass: Record<AvatarSize, string> = {
  small: 'size-8',
  regular: 'size-9',
  large: 'size-10'
}
const pullRequestAuthorPixels: Record<AvatarSize, number> = {
  small: 36,
  regular: 40,
  large: 48
}
const pullRequestNumberPixels: Record<AvatarSize, number> = {
  small: 5.5,
  regular: 6.6,
  large: 6.6
}
const pullRequestIconPixels: Record<AvatarSize, number> = {
  small: 7.7,
  regular: 8.8,
  large: 9.9
}
const pullRequestStatusPixels: Record<AvatarSize, number> = {
  small: 14,
  regular: 16,
  large: 18
}
const runSizeClass: Record<AvatarSize, string> = {
  small: 'size-7',
  regular: 'size-8',
  large: 'size-9'
}
const runStatusSizeClass: Record<AvatarSize, string> = {
  small: 'size-6',
  regular: 'size-7',
  large: 'size-8'
}
const runIconPixels: Record<AvatarSize, number> = {
  small: 10,
  regular: 12,
  large: 14
}
const runBubbleSizeClass: Record<AvatarSize, string> = {
  small: 'size-2',
  regular: 'size-2.5',
  large: 'size-3'
}
const runBubbleFontPixels: Record<AvatarSize, number> = {
  small: 5,
  regular: 6,
  large: 7
}
const runCornerEmojiPixels: Record<AvatarSize, number> = {
  small: 14,
  regular: 16,
  large: 18
}
const runCornerStatusPixels: Record<AvatarSize, number> = {
  small: 14,
  regular: 16,
  large: 18
}
const runCornerIconPixels: Record<AvatarSize, number> = {
  small: 7,
  regular: 8,
  large: 9
}
const squircleStyle = { borderRadius: '32%' } as const
const fillCenterStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
} as const

/**
 * Pick a visible emoji pixel from a seeded starting point. Canvas emoji has a
 * transparent bounding box, so a direct coordinate sample would frequently be
 * transparent; this deterministic scan explicitly rejects those pixels.
 */
export function sampleOpaquePixel(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  seed: string,
  alphaThreshold = 32
): Rgb | undefined {
  const count = width * height
  if (width <= 0 || height <= 0 || pixels.length < count * 4) return undefined
  const start = hash(seed) % count
  for (let offset = 0; offset < count; offset += 1) {
    const index = ((start + offset) % count) * 4
    if (pixels[index + 3]! >= alphaThreshold) {
      return [pixels[index]!, pixels[index + 1]!, pixels[index + 2]!]
    }
  }
  return undefined
}

function mix(from: number, to: number, amount: number): number {
  return Math.round(from + (to - from) * amount)
}

function luminance([red, green, blue]: Rgb): number {
  const channel = (value: number) => {
    const normalized = value / 255
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue)
}

export function sampledSurface(
  color: Rgb,
  dark = false
): { backgroundColor: string; color: string } {
  // Retain enough of the sampled hue to make identities bright and distinct.
  // Dark mode also lifts the color instead of muddying it toward black.
  const target = 255
  const amount = dark ? 0.2 : 0.62
  const surface: Rgb = [
    mix(color[0], target, amount),
    mix(color[1], target, amount),
    mix(color[2], target, amount)
  ]
  return {
    backgroundColor: `rgb(${surface.join(' ')})`,
    color: luminance(surface) > 0.42 ? '#172033' : '#ffffff'
  }
}

/**
 * A bright deterministic fallback used before canvas sampling is available and
 * for entities, such as pull requests, that do not have an emoji to sample.
 * Inline color is intentional: generated Tailwind class names are not emitted
 * by the production scanner.
 */
export function fallbackSurface(
  id: string,
  dark = false
): { backgroundColor: string; color: string } {
  const hue = hash(id) % 360
  return dark
    ? { backgroundColor: `hsl(${hue} 58% 34%)`, color: `hsl(${hue} 75% 94%)` }
    : { backgroundColor: `hsl(${hue} 78% 86%)`, color: `hsl(${hue} 55% 20%)` }
}

function emojiSurface(
  id: string,
  emoji: string,
  dark: boolean
): { backgroundColor: string; color: string } | undefined {
  if (typeof document === 'undefined') return undefined
  // JSDOM exposes a canvas element but intentionally has no rendering
  // context; skip it instead of producing one warning for every list row.
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) return undefined
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 64
    canvas.height = 64
    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (!context) return undefined
    context.clearRect(0, 0, 64, 64)
    context.font = '48px "Apple Color Emoji", "Segoe UI Emoji", sans-serif'
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    context.fillText(emoji, 32, 34)
    const color = sampleOpaquePixel(context.getImageData(0, 0, 64, 64).data, 64, 64, id)
    return color ? sampledSurface(color, dark) : undefined
  } catch {
    return undefined
  }
}

const themeSubscribers = new Set<() => void>()
let themeObserver: MutationObserver | undefined

function subscribeResolvedTheme(listener: () => void): () => void {
  themeSubscribers.add(listener)
  if (!themeObserver && typeof MutationObserver === 'function' && typeof document !== 'undefined') {
    themeObserver = new MutationObserver(() => {
      for (const subscriber of themeSubscribers) subscriber()
    })
    themeObserver.observe(document.documentElement, {
      attributeFilter: ['class'],
      attributes: true
    })
  }
  return () => {
    themeSubscribers.delete(listener)
    if (themeSubscribers.size === 0) {
      themeObserver?.disconnect()
      themeObserver = undefined
    }
  }
}

function resolvedDarkTheme(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
}

function useResolvedDarkTheme(): boolean {
  return useSyncExternalStore(subscribeResolvedTheme, resolvedDarkTheme, () => false)
}

function useEmojiSurface(id: string, emoji: string) {
  const [surface, setSurface] = useState<{ backgroundColor: string; color: string }>()
  const dark = useResolvedDarkTheme()
  useEffect(() => setSurface(emojiSurface(id, emoji, dark)), [dark, id, emoji])
  return surface ?? fallbackSurface(id, dark)
}

export function AvatarTile({
  id,
  emoji,
  size,
  label,
  className
}: {
  id: string
  emoji: string
  size: AvatarSize
  label?: string
  className?: string
}) {
  const surface = useEmojiSurface(id, emoji)
  return (
    <span
      data-avatar-id={id}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      style={{ ...squircleStyle, ...surface }}
      className={cn(
        'relative inline-block shrink-0',
        avatarSquircleClass,
        tileSizeClass[size],
        className
      )}
    >
      <span style={fillCenterStyle} className="leading-none select-none">
        <span className="translate-y-px">{emoji}</span>
      </span>
    </span>
  )
}

export function SuiteAvatarTile({
  id,
  emojis,
  size,
  label
}: {
  id: string
  emojis: string[]
  size: AvatarSize
  label?: string
}) {
  const cells = [...new Set([...emojis, ...fallbackEmojis])].slice(0, 4)
  const surface = useEmojiSurface(id, cells.join(''))
  return (
    <span
      data-avatar-id={id}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      style={{ ...squircleStyle, ...surface }}
      className={cn('relative inline-block shrink-0', avatarSquircleClass, tileSizeClass[size])}
    >
      <span style={fillCenterStyle}>
        <span
          style={{
            width: suiteClusterPixels[size],
            height: suiteClusterPixels[size],
            fontSize: suiteEmojiPixels[size],
            lineHeight: 1
          }}
          className="grid grid-cols-2 grid-rows-2 place-items-center gap-px"
        >
          {cells.map((emoji, index) => (
            <span
              key={`${emoji}-${index}`}
              style={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}
              className="flex items-center justify-center select-none"
            >
              {emoji}
            </span>
          ))}
        </span>
      </span>
    </span>
  )
}

export function PullRequestAvatarTile({
  pullRequest,
  status,
  size,
  label
}: {
  pullRequest?: number
  status?: 'draft' | 'ready' | 'merged' | 'closed'
  size: AvatarSize
  label: string
}) {
  const presentation =
    status === 'merged'
      ? {
          icon: GitMerge,
          tone: 'text-violet-400',
          surface: 'bg-status-infra-surface',
          color: '#a78bfa'
        }
      : status === 'closed'
        ? {
            icon: GitPullRequestClosed,
            tone: 'text-rose-400',
            surface: 'bg-status-fail-surface',
            color: '#fb7185'
          }
        : status === 'ready'
          ? {
              icon: GitPullRequest,
              tone: 'text-emerald-400',
              surface: 'bg-status-pass-surface',
              color: '#34d399'
            }
          : {
              icon: GitPullRequestDraft,
              tone: 'text-zinc-300',
              surface: 'bg-status-neutral-surface',
              color: '#d4d4d8'
            }
  const Icon = presentation.icon
  const id = `pull-request:${pullRequest ?? 'unknown'}`
  const identityColor = fallbackSurface(id).backgroundColor
  return (
    <span
      data-avatar-id={id}
      aria-hidden="true"
      title={label}
      style={{ ...squircleStyle, backgroundColor: '#27272a' }}
      className={cn(
        'relative inline-block shrink-0',
        avatarSquircleClass,
        pullRequestSizeClass[size]
      )}
    >
      <span
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: pullRequestNumberPixels[size],
          color: identityColor
        }}
        className="font-mono leading-none font-medium tracking-[-0.03em] opacity-90 tabular-nums"
      >
        {pullRequest ?? 'PR'}
      </span>
      <span
        style={{
          position: 'absolute',
          right: -1,
          bottom: -1,
          width: pullRequestStatusPixels[size],
          height: pullRequestStatusPixels[size],
          color: presentation.color,
          borderColor: presentation.color,
          borderStyle: 'solid',
          borderWidth: 1.5
        }}
        className={cn(
          'pr-avatar-status flex items-center justify-center rounded-full shadow-sm',
          presentation.surface,
          presentation.tone
        )}
      >
        <Icon
          aria-hidden
          style={{ width: pullRequestIconPixels[size], height: pullRequestIconPixels[size] }}
          strokeWidth={2.25}
        />
      </span>
    </span>
  )
}

export function PullRequestAuthorAvatar({
  author,
  size,
  label
}: {
  author?: PullRequestMetadata['author']
  size: AvatarSize
  label: string
}) {
  const [imageFailed, setImageFailed] = useState(false)
  useEffect(() => setImageFailed(false), [author?.avatarUrl])

  return (
    <span
      role="img"
      aria-label={author ? `${label}, opened by ${author.login}` : `${label}, author unavailable`}
      style={{ width: pullRequestAuthorPixels[size], height: pullRequestAuthorPixels[size] }}
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-muted-foreground"
    >
      {author && !imageFailed ? (
        <img
          src={author.avatarUrl}
          alt=""
          className="size-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <UserRound aria-hidden className="size-1/2" strokeWidth={1.75} />
      )}
    </span>
  )
}

export function RunAvatar({
  run,
  emoji,
  size,
  variant
}: {
  run: WebTestRun
  emoji: string
  size: AvatarSize
  variant: RunAvatarVariant
}) {
  const verdict = runVerdict(run)
  const surface = useEmojiSurface(run.scenarioId, emoji)
  const presentation = verdict.active
    ? {
        icon: LoaderCircle,
        tone: 'text-status-info',
        surface: 'bg-status-info-surface',
        statusColor: 'var(--status-info)',
        surfaceColor: 'var(--status-info-surface)'
      }
    : run.classification === 'test-drift'
      ? {
          icon: ScanSearch,
          tone: 'text-status-warn',
          surface: 'bg-status-warn-surface',
          statusColor: 'var(--status-warn)',
          surfaceColor: 'var(--status-warn-surface)'
        }
      : run.status === 'infrastructure-error' || run.classification === 'infrastructure-error'
        ? {
            icon: Zap,
            tone: 'text-status-infra',
            surface: 'bg-status-infra-surface',
            statusColor: 'var(--status-infra)',
            surfaceColor: 'var(--status-infra-surface)'
          }
        : run.status === 'failed'
          ? {
              icon: X,
              tone: 'text-status-fail',
              surface: 'bg-status-fail-surface',
              statusColor: 'var(--status-fail)',
              surfaceColor: 'var(--status-fail-surface)'
            }
          : run.status === 'passed'
            ? {
                icon: Check,
                tone: 'text-status-pass',
                surface: 'bg-status-pass-surface',
                statusColor: 'var(--status-pass)',
                surfaceColor: 'var(--status-pass-surface)'
              }
            : run.status === 'timed-out'
              ? {
                  icon: TimerOff,
                  tone: 'text-status-warn',
                  surface: 'bg-status-warn-surface',
                  statusColor: 'var(--status-warn)',
                  surfaceColor: 'var(--status-warn-surface)'
                }
              : run.status === 'cancelled'
                ? {
                    icon: Ban,
                    tone: 'text-status-neutral',
                    surface: 'bg-status-neutral-surface',
                    statusColor: 'var(--status-neutral)',
                    surfaceColor: 'var(--status-neutral-surface)'
                  }
                : {
                    icon: Eye,
                    tone: 'text-status-warn',
                    surface: 'bg-status-warn-surface',
                    statusColor: 'var(--status-warn)',
                    surfaceColor: 'var(--status-warn-surface)'
                  }
  const Icon = presentation.icon

  if (variant === 'corner') {
    const sharedBorder = {
      borderColor: presentation.statusColor,
      borderStyle: 'solid' as const,
      borderWidth: 1.5
    }
    return (
      <span
        data-avatar-id={run.scenarioId}
        data-run-avatar-variant="corner"
        aria-hidden="true"
        className={cn('relative inline-block shrink-0', runSizeClass[size])}
      >
        <span
          style={{
            ...fillCenterStyle,
            ...surface,
            fontSize: runCornerEmojiPixels[size],
            ...sharedBorder
          }}
          className="run-corner-scenario overflow-hidden rounded-full leading-none"
        >
          <span className="-translate-y-[0.04em]">{emoji}</span>
        </span>
        <span
          data-run-status="corner"
          style={{
            width: runCornerStatusPixels[size],
            height: runCornerStatusPixels[size],
            right: -1,
            bottom: -1,
            color: presentation.statusColor,
            backgroundColor: `color-mix(in oklch, ${presentation.statusColor} 28%, ${presentation.surfaceColor})`,
            ...sharedBorder
          }}
          className={cn(
            'run-corner-status absolute z-10 flex items-center justify-center rounded-full shadow-sm',
            presentation.surface,
            presentation.tone
          )}
        >
          <Icon
            aria-hidden
            style={{ width: runCornerIconPixels[size], height: runCornerIconPixels[size] }}
            className={cn(
              'run-status-icon',
              verdict.active && 'avatar-status-spinner animate-spin motion-reduce:animate-none'
            )}
            strokeWidth={2.5}
          />
        </span>
      </span>
    )
  }

  return (
    <span
      data-avatar-id={run.scenarioId}
      data-run-avatar-variant="centered"
      aria-hidden="true"
      className={cn('relative inline-block shrink-0', runSizeClass[size])}
    >
      <span
        data-run-status="overlay"
        style={{ ...fillCenterStyle, width: 'auto', height: 'auto' }}
        className={cn(
          'run-status-surface z-0 m-auto rounded-full',
          runStatusSizeClass[size],
          presentation.surface
        )}
      />
      <span
        style={{
          ...surface,
          fontSize: runBubbleFontPixels[size],
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        className={cn(
          'run-scenario-bubble z-10 overflow-hidden rounded-full leading-none',
          runBubbleSizeClass[size]
        )}
      >
        <span className="-translate-y-[0.04em]">{emoji}</span>
      </span>
      <span style={fillCenterStyle} className={cn('run-status-icon-layer z-20', presentation.tone)}>
        <Icon
          aria-hidden
          style={{ width: runIconPixels[size], height: runIconPixels[size] }}
          className={cn(
            'run-status-icon',
            verdict.active && 'avatar-status-spinner animate-spin motion-reduce:animate-none'
          )}
          strokeWidth={2.5}
        />
      </span>
    </span>
  )
}
